import React, { useState, useEffect } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api.ts";
import { formatPrice } from "../../utils/functions.js";
import { X, Upload, FileText } from "lucide-react";
import placeholder from "../../assets/placeholder.svg";
import PaymentPopup from "../common/PaymentPopup";
import Pagination from "../common/Pagination";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const WonVehicles = () => {
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wonVehicles, setWonVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [auctionType, setAuctionType] = useState("auction"); // Default to auction
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchLotNumber, setSearchLotNumber] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [downloadingVehicleId, setDownloadingVehicleId] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [tabCounts, setTabCounts] = useState({
    all: 0,
    pending: 0,
    "partial-payment": 0,
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchLotNumber);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchLotNumber]);

  // Fetch counts for each tab from server
  useEffect(() => {
    const fetchTabCounts = async () => {
      try {
        // Base params for all queries - using flat filter structure like the API expects
        const baseParams = {
          page: 1,
          limit: 1, // Just need the total count, not the data
          orderBy: "id",
          order: "desc",
          "filters[isSold]": "1",
          "filters[isFromBuyNow]": auctionType === "buynow" ? "1" : "0",
        };

        // Fetch all counts in parallel
        const [
          allResponse,
          pendingResponse,
          partialResponse,
          paidResponse,
          underApprovalResponse,
        ] = await Promise.all([
          // All - no status filter
          api.get(`/users/mywonvehicles`, {
            params: baseParams,
          }),
          // Pending - invoicePaymentStatus = 1
          api.get(`/users/mywonvehicles`, {
            params: {
              ...baseParams,
              "filters[invoicePaymentStatus]": 1,
            },
          }),
          // Partial Payment - invoicePaymentStatus = 2
          api.get(`/users/mywonvehicles`, {
            params: {
              ...baseParams,
              "filters[invoicePaymentStatus]": 2,
            },
          }),
          // Paid - invoicePaymentStatus = 3
          api.get(`/users/mywonvehicles`, {
            params: {
              ...baseParams,
              "filters[invoicePaymentStatus]": 3,
            },
          }),
          // Under Approval
          api.get(`/users/mywonvehicles`, {
            params: {
              ...baseParams,
              "filters[isUnderApproval]": 1,
            },
          }),
        ]);

        // Extract counts from responses
        setTabCounts({
          all: allResponse.data?.total || 0,
          pending: pendingResponse.data?.total || 0,
          "partial-payment": partialResponse.data?.total || 0,
          paid: paidResponse.data?.total || 0,
        });
      } catch (error) {
        console.error("Error fetching tab counts:", error);
        // Set counts to 0 on error
        setTabCounts({
          all: 0,
          paid: 0,
          pending: 0,
          "partial-payment": 0,
        });
      }
    };

    fetchTabCounts();
  }, [auctionType]);

  // Fetch won vehicles from API with server-side pagination
  useEffect(() => {
    const fetchWonVehicles = async () => {
      try {
        setLoading(true);

        // Build query parameters for server-side pagination and filtering
        const params = {
          page: currentPage,
          limit: pageSize,
          orderBy: "id",
          order: "desc",
          "filters[isSold]": "1",
          "filters[isFromBuyNow]": auctionType === "buynow" ? "1" : "0",
        };

        // Add status filter if not "all"
        if (filterStatus !== "all") {
          // Map status strings to their corresponding IDs from the database
          const statusIdMap = {
            pending: 1,
            "partial-payment": 2,
            paid: 3,
          };

          if (statusIdMap[filterStatus]) {
            params["filters[invoicePaymentStatus]"] = statusIdMap[filterStatus];
          } else if (filterStatus === "under-approval") {
            params["filters[isUnderApproval]"] = 1;
          }
        }

        // Add search parameter if provided
        if (debouncedSearch && debouncedSearch.trim() !== "") {
          params.search = debouncedSearch.trim();
        }

        const response = await api.get(`/users/mywonvehicles`, { params });

        // Extract the actual data array from the paginated response
        const vehiclesData = response.data.data || response.data;

        // Check if vehiclesData is an array
        if (!Array.isArray(vehiclesData)) {
          console.error("API response data is not an array:", vehiclesData);
          setWonVehicles([]);
          setLoading(false);
          return;
        }

        setWonVehicles(vehiclesData);

        // Update pagination state from API response
        if (response.data.page && response.data.pages && response.data.total) {
          setPagination({
            currentPage: response.data.page,
            totalPages: response.data.pages,
            totalItems: response.data.total,
            itemsPerPage: pageSize,
          });
        } else {
          console.log("No pagination data in API response");
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching won vehicles:", error);
        setLoading(false);
        // Set empty array on error
        setWonVehicles([]);
      }
    };

    fetchWonVehicles();
  }, [currentPage, filterStatus, debouncedSearch, pageSize, auctionType]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getUnderApprovalAmount = (vehicle) => {
    const payments =
      vehicle?.buyerInvoiceDetails?.[0]?.buyerInvoice?.buyerInvoicePayments ||
      vehicle?.buyerInvoicePayments ||
      [];

    return (payments || []).reduce((sum, payment) => {
      const amount = Number(payment?.amountPaid || 0);
      if (payment?.isApproved === null && amount > 0) {
        return sum + amount;
      }
      return sum;
    }, 0);
  };

  const getStatusColor = (vehicle) => {
    const status = getVehicleStatus(vehicle);
    if (status === "under-approval") {
      return "text-white bg-blue-600";
    }

    const invoice = vehicle.buyerInvoiceDetails[0]?.buyerInvoice;
    const statusName = invoice?.invoicePaymentStatus?.name;

    if (statusName === "Paid") {
      return "text-white bg-green-700";
    } else if (statusName === "Pending") {
      return "text-white bg-yellow-700";
    } else if (statusName === "Partial Payment") {
      return "text-white bg-orange-700";
    }
    return "text-white bg-gray-700";
  };

  const getStatusText = (vehicle) => {
    const status = getVehicleStatus(vehicle);
    if (status === "under-approval") {
      return translate("wonVehicles.tabs.underApproval") || "Under Approval";
    }

    const invoice = vehicle.buyerInvoiceDetails[0]?.buyerInvoice;
    const statusName = invoice?.invoicePaymentStatus?.name;

    if (statusName === "Paid") {
      return translate("wonVehicles.tabs.paid");
    } else if (statusName === "Pending") {
      return translate("wonVehicles.tabs.pendingPayment");
    } else if (statusName === "Partial Payment") {
      return translate("wonVehicles.tabs.partialPayment");
    }
    return statusName || translate("wonVehicles.tabs.allVehicles");
  };

  // const getVehicleStatus = (vehicle) => {
  //   return (
  //     vehicle.buyerInvoiceDetails[0]?.buyerInvoice?.invoicePaymentStatus
  //       ?.slug || "unknown"
  //   );
  // };
  const getVehicleStatus = (vehicle) => {
    const underApprovalAmount = getUnderApprovalAmount(vehicle);
    if (underApprovalAmount > 0) {
      return "under-approval";
    }

    const invoice = vehicle?.buyerInvoiceDetails?.[0]?.buyerInvoice || {};
    const statusSlug = invoice?.invoicePaymentStatus?.slug;
    return statusSlug || "unknown";
  };

  // Payment popup functions
  const handlePayNow = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowPaymentPopup(true);
  };

  const handleClosePopup = () => {
    setShowPaymentPopup(false);
    setSelectedVehicle(null);
  };

  const handlePaymentSuccess = () => {
    // Refresh the vehicles list after successful payment
    window.location.reload();
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, debouncedSearch]);
  //==================tuesday fix  download documents for won vehicles
  // const downloadFile = async (url, filename) => {
  //   try {
  //     const response = await fetch(url);
  //     const blob = await response.blob();

  //     const link = document.createElement("a");
  //     link.href = window.URL.createObjectURL(blob);
  //     link.download = filename || "document";
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   } catch (err) {
  //     console.error("Download failed:", err);
  //   }
  // };
  // const handleDownloadAllDocuments = async (vehicle) => {
  //   const docs = vehicle?.documents || [];

  //   if (!docs.length) return;

  //   for (const doc of docs) {
  //     await downloadFile(doc.url, doc.name);
  //   }
  // };

  //================== tuesday fix (UPDATED - no CORS issue)
const cleanFileName = (name = "") => {
  return name
    .split("/").pop() // 🔥 removes any folder path
    .replace(/\s+/g, "_")
    .replace(/[\/\\:*?"<>|]/g, "")
    .replace(/%20/g, "_")
    .trim();
};

const downloadAllAsZip = async (vehicle) => {
  try {
    setDownloadingVehicleId(vehicle.id);

    const docs = vehicle?.documents || [];
    if (!docs.length) return;

    const zip = new JSZip();

    // 🔥 CLEAN ROOT FOLDER (NO SLASHES EVER)
    const folderName = `${vehicle?.name || "Vehicle"}_Lot_${vehicle?.lotNumber || "NA"}`
      .replace(/\//g, "")
      .replace(/\\/g, "")
      .replace(/[<>:"|?*]/g, "")
      .trim();

    const folder = zip.folder(folderName);

    for (const doc of docs) {
      try {
        const response = await fetch(doc.url);
        const blob = await response.blob();

        // 🔥 CRITICAL FIX: remove any folder structure from API name
        let fileName = cleanFileName(doc.name);

        // 🔥 prevent accidental duplication of folder name inside file name
        fileName = fileName.replaceAll(folderName, "").trim();

        folder.file(fileName, blob);
      } catch (err) {
        console.error("File failed:", doc.url, err);
      }
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });

    saveAs(zipBlob, `${folderName}.zip`);
  } catch (err) {
    console.error("ZIP download failed:", err);
  } finally {
    setDownloadingVehicleId(null);
  }
};
  //=========== tuesday end =====================

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <>
      <div
        className="bg-[#FFF9F9] rounded-lg shadow-md"
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Header */}
        <div className="bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[16px] px-1 py-1">
          <div className="p-[20px] text-lg rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2">
            <h2 className="text-base sm:text-lg font-semibold">
              {translate("wonVehicles.heading")}
            </h2>
          </div>
        </div>

        {/* Auction Type Tabs */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-5 py-2">
            <div className="bg-gray-50 border border-gray-100 w-fit p-2 rounded-lg">
              <nav
                className={`flex ${isRTL ? "space-x-reverse" : "space-x-1"}`}
              >
                {[
                  {
                    key: "auction",
                    label: translate("wonVehicles.auctionTabs.auction"),
                  },
                  {
                    key: "buynow",
                    label: translate("wonVehicles.auctionTabs.buyNow"),
                  },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setAuctionType(tab.key)}
                    className={`px-6 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                      auctionType === tab.key
                        ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-lg"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {/* Filter Navigation */}
          <div className="border-b border-gray-200">
            {/* Filter Tabs and Search */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 py-4 px-5">
              {/* Filter Tabs */}
              <nav
                className={`flex ${
                  isRTL ? "space-x-reverse" : "space-x-2"
                } overflow-x-auto pb-2 lg:pb-0`}
              >
                {[
                  {
                    key: "all",
                    label: translate("wonVehicles.tabs.allVehicles"),
                    count: tabCounts.all,
                  },
                  {
                    key: "paid",
                    label: translate("wonVehicles.tabs.paid"),
                    count: tabCounts.paid,
                  },
                  {
                    key: "pending",
                    label: translate("wonVehicles.tabs.pendingPayment"),
                    count: tabCounts.pending,
                  },
                  {
                    key: "partial-payment",
                    label: translate("wonVehicles.tabs.partialPayment"),
                    count: tabCounts["partial-payment"],
                  },
                ].map((filter) => {
                  return (
                    <button
                      key={filter.key}
                      onClick={() => setFilterStatus(filter.key)}
                      className={`flex-shrink-0 px-3 py-2 rounded-full font-medium text-sm transition-all duration-200 flex items-center gap-2
            ${
              filterStatus === filter.key
                ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
            }`}
                    >
                      <span>{filter.label}</span>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                          filterStatus === filter.key
                            ? "bg-white/20 text-white"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {filter.count}
                      </span>
                    </button>
                  );
                })}
              </nav>

              {/* Search Field */}
              <div className="flex items-center">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={translate(
                      "wonVehicles.filters.searchByLotNumber",
                    )}
                    className={`w-full sm:w-80 px-4 py-2 ${
                      isRTL ? "pr-10" : "pl-10"
                    } border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm`}
                    value={searchLotNumber || ""}
                    onChange={(e) => setSearchLotNumber(e.target.value)}
                  />
                  <svg
                    className={`absolute ${
                      isRTL ? "right-3" : "left-3"
                    } top-1/2 transform -translate-y-1/2 w-4 h-4 text-orange-500`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {searchLotNumber && (
                  <button
                    onClick={() => setSearchLotNumber("")}
                    className="ml-2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Clear search"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Vehicles List */}
          <div className="space-y-3">
            {wonVehicles.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {translate("wonVehicles.messages.noWonVehiclesFound")}
                </h3>
                <p className="text-gray-500">
                  {translate("wonVehicles.messages.noWonVehiclesYet")}
                </p>
              </div>
            ) : (
              wonVehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className="bg-white rounded-lg shadow-sm p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-3">
                    {/* Vehicle Image */}
                    <div className="flex-shrink-0">
                      <img
                        src={
                          (vehicle?.images &&
                            vehicle?.images?.length > 0 &&
                            vehicle?.images[0]?.url) ||
                          vehicle?.images[0]?.imageUrl ||
                          placeholder
                        }
                        alt={vehicle?.name}
                        className="w-full sm:w-20 h-32 sm:h-16 object-cover rounded-md"
                      />
                    </div>

                    {/* Vehicle Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col lg:flex-row lg:items-start space-y-2 lg:space-y-0 lg:space-x-4">
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-black mb-1 ">
                            {vehicle?.name}
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500">
                                {translate("wonVehicles.labels.lotNumber")}:
                              </span>
                              <span
                                className={`${
                                  isRTL ? "mr-2" : "ml-2"
                                } font-medium text-gray-900`}
                              >
                                {vehicle?.lotNumber}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                {translate("wonVehicles.labels.invoiceNumber")}:
                              </span>
                              <span
                                className={`${
                                  isRTL ? "mr-2" : "ml-2"
                                } font-medium text-gray-900`}
                              >
                                {vehicle?.buyerInvoiceDetails[0]?.buyerInvoice
                                  ?.invoiceNumber ||
                                  vehicle?.buyerInvoiceDetails[0]?.buyerInvoice
                                    ?.id ||
                                  "N/A"}
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-500">
                                {translate("wonVehicles.labels.winningBid")}:
                              </span>
                              <span
                                className={`${
                                  isRTL ? "mr-2" : "ml-2"
                                } font-medium text-gray-900`}
                              >
                                {formatPrice(vehicle?.soldPrice)}
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-500">
                                {translate("wonVehicles.labels.invoiceAmount")}:
                              </span>
                              <span
                                className={`${
                                  isRTL ? "mr-2" : "ml-2"
                                } font-medium text-gray-900`}
                              >
                                {formatPrice(
                                  vehicle?.buyerInvoiceDetails[0]?.buyerInvoice
                                    ?.totalAmount,
                                )}{" "}
                                ({translate("wonVehicles.labels.vatIncluded")})
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-500">
                                {translate(
                                  "wonVehicles.labels.remainingAmount",
                                )}
                                :
                              </span>
                              <span
                                className={`${
                                  isRTL ? "mr-2" : "ml-2"
                                } font-medium text-gray-900`}
                              >
                                {formatPrice(
                                  vehicle?.buyerInvoiceDetails[0]?.buyerInvoice
                                    ?.remainingAmount,
                                )}{" "}
                              </span>

                              {getUnderApprovalAmount(vehicle) > 0 && (
                                <div className="mt-1">
                                  <span className="text-gray-500">
                                    {translate(
                                      "wonVehicles.tabs.underApproval",
                                    ) || "Under Approval"}
                                    :
                                  </span>
                                  <span
                                    className={`${
                                      isRTL ? "mr-2" : "ml-2"
                                    } font-medium text-blue-600`}
                                  >
                                    {formatPrice(
                                      getUnderApprovalAmount(vehicle),
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* <div>
                          <span className="text-gray-500">VIN:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {vehicle?.vinNumber
                              ? vehicle?.vinNumber.length > 5
                                ? "************" + vehicle?.vinNumber.slice(-5)
                                : vehicle?.vinNumber
                              : "N/A"}
                          </span>
                        </div> */}

                            <div>
                              <span className="text-gray-500">
                                {translate("wonVehicles.labels.soldDate")}:
                              </span>
                              <span
                                className={`${
                                  isRTL ? "mr-2" : "ml-2"
                                } font-medium text-gray-900`}
                              >
                                {formatDate(vehicle?.soldDate)}
                              </span>
                            </div>

                            <div>
                              <span className="text-gray-500">
                                {translate("wonVehicles.labels.status")}:
                              </span>
                              <span
                                className={`${
                                  isRTL ? "mr-2" : "ml-2"
                                } px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                  vehicle,
                                )}`}
                              >
                                {getStatusText(vehicle)}
                              </span>
                            </div>

                            {/* {getVehicleStatus(vehicle) === 'ready_pickup' && (
                          <div>
                            <span className="text-gray-500">Pickup Date:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {formatDate(new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString())}
                            </span>
                          </div>
                        )} */}
                          </div>
                        </div>

                        {/* Action Buttons - Moved to right end */}
                        <div className="flex-shrink-0 lg:w-40">
                          <div className="flex flex-col space-y-1">
                            {(getVehicleStatus(vehicle) === "partial-payment" ||
                              getVehicleStatus(vehicle) === "pending") && (
                              <button
                                onClick={() => handlePayNow(vehicle)}
                                disabled={
                                  getVehicleStatus(vehicle) === "under-approval"
                                }
                                className={`w-full text-white px-3 py-1.5 rounded-md transition-colors text-sm ${
                                  getVehicleStatus(vehicle) === "under-approval"
                                    ? "bg-orange-300 cursor-not-allowed"
                                    : "bg-orange-500 hover:bg-orange-600"
                                }`}
                              >
                                {translate("wonVehicles.buttons.payNow")}
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/${vehicle.slug}`)}
                              className="w-full border bg-orange-100 border-orange-300 text-gray-700 px-3 py-1.5 rounded-md hover:bg-orange-500 transition-colors text-sm"
                            >
                              {translate("wonVehicles.buttons.viewDetails")}
                            </button>
                            {/* //tuesday fix  download documents for won vehicles */}

                            {vehicle?.documents?.length > 0 && (
                              <button
                                onClick={() => downloadAllAsZip(vehicle)}
                                disabled={downloadingVehicleId === vehicle.id}
                                className="w-full border border-orange-300 text-gray-700 px-3 py-1.5 rounded-md hover:bg-orange-50 transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                              >
                                {downloadingVehicleId === vehicle.id ? (
                                  <>
                                    <svg
                                      className="w-4 h-4 animate-spin"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        fill="none"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                                      />
                                    </svg>
                                    Downloading...
                                  </>
                                ) : (
                                  translate(
                                    "wonVehicles.buttons.downloadDocuments",
                                  )
                                )}
                              </button>
                            )}

                            {/* //tuesday fix  end  */}
                          </div>
                        </div>
                      </div>

                      {/* Status Messages */}
                      {getVehicleStatus(vehicle) === "partial-payment" && (
                        <div
                          className={`mt-3 flex items-center text-orange-600 ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          <svg
                            className={`w-4 h-4 ${isRTL ? "ml-2" : "mr-2"}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {translate(
                              "wonVehicles.messages.partialPaymentReceived",
                            )}
                          </span>
                        </div>
                      )}

                      {getVehicleStatus(vehicle) === "under-approval" && (
                        <div
                          className={`mt-3 flex items-center text-blue-600 ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          <svg
                            className={`w-4 h-4 ${isRTL ? "ml-2" : "mr-2"}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {translate("wonVehicles.tabs.underApproval") ||
                              "Under Approval"}
                          </span>
                        </div>
                      )}

                      {getVehicleStatus(vehicle) === "pending" && (
                        <div
                          className={`mt-3 flex items-center text-red-600 ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          <svg
                            className={`w-4 h-4 ${isRTL ? "ml-2" : "mr-2"}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {translate("wonVehicles.messages.paymentDue")}
                          </span>
                        </div>
                      )}

                      {getVehicleStatus(vehicle) === "paid" && (
                        <div
                          className={`mt-3 flex items-center text-green-600 ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          <svg
                            className={`w-4 h-4 ${isRTL ? "ml-2" : "mr-2"}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            {translate("wonVehicles.messages.paymentCompleted")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <Pagination
                totalPages={pagination.totalPages}
                currentPage={pagination.currentPage}
                setCurrentPage={handlePageChange}
              />
            </div>
          )}

          {/* Payment Receipt Upload Popup */}
          {/* Payment Popup */}
        </div>
      </div>
      <PaymentPopup
        isOpen={showPaymentPopup}
        onClose={handleClosePopup}
        selectedItem={selectedVehicle}
        itemType="vehicle"
        onPaymentSuccess={handlePaymentSuccess}
      />
    </>
  );
};

export default WonVehicles;
